import React, {FC, useMemo} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import type {
  QHSENearMissReportDto,
  NearMissOccurrenceType,
  NearMissLocationOfOccurrence,
  NearMissSubstandardAct,
  NearMissSubstandardCondition,
} from '../core/_models'

type Props = {
  visible: boolean
  record: QHSENearMissReportDto | null
  onCancel: () => void
  onOpenSupporting?: (id: number, reportNumber?: string | null) => void
  onDownloadPdf?: (id: number) => void
  downloadingPdf?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const occurrenceTypeLabel = (t: NearMissOccurrenceType): string => {
  switch (t) {
    case 'ACCIDENT':
      return 'Accident'
    case 'INCIDENT':
      return 'Incident'
    case 'NEAR_MISS':
      return 'Near miss'
    case 'HIGH_SEVERITY_NEAR_MISS':
      return 'High severity near miss'
    default:
      return t
  }
}

const locationOfOccurrenceLabel = (loc: NearMissLocationOfOccurrence): string => {
  switch (loc) {
    case 'ACCOMMODATION':
      return 'Accommodation'
    case 'BRIDGE':
      return 'Bridge'
    case 'ASHORE':
      return 'Ashore'
    case 'ENGINE_ROOM_MACHINERY_SPACES':
      return 'Engine Room / Machinery Spaces'
    case 'DECK':
      return 'Deck'
    case 'ALOFT_OVERBOARD':
      return 'Aloft / Overboard'
    case 'ENCLOSED_SPACES':
      return 'Enclosed Spaces'
    case 'ENCLOSED_SPACES_MACHINERY':
      return 'Enclosed Spaces (Machinery)'
    case 'OFFICE':
      return 'Office'
    case 'STORE':
      return 'Store'
    case 'TRAFFIC_ROAD':
      return 'Traffic Road'
    case 'OTHER':
      return 'Other'
    default:
      return loc
  }
}

const substandardActLabel = (a: NearMissSubstandardAct): string => {
  switch (a) {
    case 'FAILURE_TO_FOLLOW_RULES':
      return 'Failure To Follow Rules'
    case 'FAILURE_TO_USE_PPE_PROPERLY':
      return 'Failure To Use PPE Properly'
    case 'OPERATING_EQUIPMENT_WITHOUT_AUTHORITY':
      return 'Operating Equipment Without Authority'
    case 'INCORRECT_USE_OF_EQUIPMENT_OR_MACHINERY':
      return 'Incorrect Use of Equipment / Machinery'
    case 'USE_OF_DEFECTIVE_EQUIPMENT_OR_MACHINERY':
      return 'Use Of Defective Equipment / Machinery'
    case 'FAILURE_TO_FOLLOW_REPAIR_OR_MAINTENANCE_INSTRUCTIONS':
      return 'Failure To Follow Repair / Maintenance Instructions'
    case 'FAILURE_TO_WARN':
      return 'Failure To Warn'
    case 'FAILURE_TO_SECURE':
      return 'Failure To Secure'
    case 'BY_PASSING_SAFETY_DEVICE':
      return 'By-Passing Safety Device'
    case 'IMPROPER_POSITION_FOR_TASK':
      return 'Improper Position for Task'
    case 'IMPROPER_LIFTING_HANDLING_OR_STORAGE':
      return 'Improper Lifting, Handling or Storage'
    case 'HORSEPLAY_OR_INAPPROPRIATE_BEHAVIOUR':
      return 'Horseplay / Inappropriate Behaviour'
    case 'UNDER_INFLUENCE_OF_ALCOHOL_OR_DRUGS':
      return 'Under Influence of Alcohol or Drugs'
    default:
      return a
  }
}

const substandardConditionLabel = (c: NearMissSubstandardCondition): string => {
  switch (c) {
    case 'INADEQUATE_GUARDS_OR_BARRIERS':
      return 'Inadequate Guards or Barriers'
    case 'INADEQUATE_OR_DEFECTIVE_PPE':
      return 'Inadequate Or Defective PPE'
    case 'DEFECTIVE_EQUIPMENT_OR_MACHINERY':
      return 'Defective Equipment / Machinery'
    case 'UNFAVOURABLE_HULL_OR_STRUCTURE_CONDITION':
      return 'Unfavourable Hull or Structure Condition'
    case 'POOR_HOUSEKEEPING':
      return 'Poor Housekeeping'
    case 'CONGESTION_OR_RESTRICTED_ACTION':
      return 'Congestion or Restricted Action'
    case 'INADEQUATE_OR_EXCESS_ILLUMINATION':
      return 'Inadequate or Excess Illumination'
    case 'INADEQUATE_VENTILATION':
      return 'Inadequate Ventilation'
    case 'OTHER_FACTORS':
      return 'Other Factors'
    default:
      return c
  }
}

export const ViewNearMissModal: FC<Props> = ({
  visible,
  record,
  onCancel,
  onOpenSupporting,
  onDownloadPdf,
  downloadingPdf = false,
}) => {
  const statusLabel = useMemo(() => {
    if (!record?.status) return '-'
    switch (record.status) {
      case 'SAVE':
        return 'Saved'
      case 'SUBMIT':
        return 'Submitted'
      case 'CLOSE':
        if (record.closureResult === 'TO_BE_REVIEWED_NEXT_INSPECTION') {
          return 'Closed and TBR'
        }
        return 'Closed Satisfactorily'
      default:
        return record.status
    }
  }, [record])

  const conditionsValue = useMemo(() => {
    const parts: string[] = []
    if (record?.substandardActs && record.substandardActs.length > 0) {
      parts.push('Unsafe Act')
    }
    if (record?.substandardConditions && record.substandardConditions.length > 0) {
      parts.push('Unsafe Condition')
    }
    return parts.length > 0 ? parts.join(', ') : '-'
  }, [record])

  const occurrenceTypesValue = useMemo(() => {
    if (!record?.occurrenceTypes || !record.occurrenceTypes.length) return '-'
    return record.occurrenceTypes.map(occurrenceTypeLabel).join(', ')
  }, [record])

  const locationValue = useMemo(() => {
    if (!record?.locationOfOccurrence || !record.locationOfOccurrence.length) return '-'
    return record.locationOfOccurrence.map(locationOfOccurrenceLabel).join(', ')
  }, [record])

  const actsValue = useMemo(() => {
    if (!record?.substandardActs || !record.substandardActs.length) return '-'
    return record.substandardActs.map(substandardActLabel).join(', ')
  }, [record])

  const conditionsSubValue = useMemo(() => {
    if (!record?.substandardConditions || !record.substandardConditions.length) return '-'
    return record.substandardConditions.map(substandardConditionLabel).join(', ')
  }, [record])

  if (!visible || !record) return null

  // Reusable PDF-style table row helper
  const renderRow = (label: string, value: React.ReactNode) => (
    <tr key={label}>
      <td
        style={{
          width: '32%',
          background: '#f8f9fa',
          fontWeight: 600,
          color: '#495057',
          padding: '7px 12px',
          border: '1px solid #dee2e6',
          fontSize: '12px',
          verticalAlign: 'top',
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: '7px 12px',
          border: '1px solid #dee2e6',
          color: '#212529',
          fontSize: '12px',
          verticalAlign: 'top',
          wordBreak: 'break-word',
        }}
      >
        {value || '-'}
      </td>
    </tr>
  )

  // Reusable PDF-style section helper
  const renderSection = (title: string, children: React.ReactNode) => (
    <div className='mb-3'>
      <table
        className='table table-bordered mb-0'
        style={{borderCollapse: 'collapse', width: '100%', borderColor: '#dee2e6'}}
      >
        <thead>
          <tr>
            <th
              colSpan={2}
              style={{
                background: '#f1f3f5',
                color: '#212529',
                fontWeight: 700,
                padding: '7px 12px',
                fontSize: '12px',
                border: '1px solid #dee2e6',
                borderBottom: '2px solid #ced4da',
                letterSpacing: '0.3px',
              }}
            >
              {title}
            </th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )

  // Reusable PDF-style description box helper
  const renderDescSection = (title: string, content: string | null | undefined) => (
    <div className='mb-3'>
      <table
        className='table table-bordered mb-0'
        style={{borderCollapse: 'collapse', width: '100%', borderColor: '#dee2e6'}}
      >
        <thead>
          <tr>
            <th
              style={{
                background: '#f1f3f5',
                color: '#212529',
                fontWeight: 700,
                padding: '7px 12px',
                fontSize: '12px',
                border: '1px solid #dee2e6',
                borderBottom: '2px solid #ced4da',
                letterSpacing: '0.3px',
              }}
            >
              {title}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                padding: '10px 14px',
                border: '1px solid #dee2e6',
                color: '#212529',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6',
                background: '#fff',
              }}
            >
              {content || '-'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '80vw',
          height: '85vh',
          maxWidth: '1200px',
          maxHeight: '92vh',
        }}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0 bg-light'>
          <div className='d-flex flex-column'>
            <h5 className='modal-title text-dark mb-1'>
              Near Miss Report &ndash; {record.reportNumber || '-'}
            </h5>
            <div className='text-muted fs-7'>
              Vessel:&nbsp;
              <span className='fw-semibold text-dark'>
                {record.vesselName || '-'}
              </span>
              {record.dateOfOccurrence && (
                <>
                  <span className='mx-2'>&bull;</span>
                  Occurrence Date:&nbsp;
                  <span className='fw-semibold text-dark'>
                    {record.dateOfOccurrence}
                    {record.timeOfOccurrence && (
                      <span className='text-muted ms-1'>
                        {record.timeOfOccurrence}
                      </span>
                    )}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className='d-flex align-items-center gap-2'>
            <span className='badge badge-light-primary fs-8 py-2 px-3'>
              {statusLabel}
            </span>

            {onDownloadPdf && record.id && (
              <button
                type='button'
                className='btn btn-light-success btn-sm d-flex align-items-center'
                onClick={() => onDownloadPdf(Number(record.id))}
                disabled={downloadingPdf}
                title='Download Individual Report PDF'
              >
                {downloadingPdf ? (
                  <span className='spinner-border spinner-border-sm me-1' />
                ) : (
                  <KTSVG
                    path='/media/icons/duotune/files/fil021.svg'
                    className='svg-icon-4 me-1'
                  />
                )}
                Download PDF
              </button>
            )}

            {record.supportingDocsPrimaryPath && onOpenSupporting && record.id && (
              <button
                type='button'
                className='btn btn-light-primary btn-sm d-flex align-items-center'
                onClick={() => onOpenSupporting(record.id!, record.reportNumber ?? null)}
              >
                <KTSVG
                  path='/media/icons/duotune/files/fil003.svg'
                  className='svg-icon-4 me-1'
                />
                Supporting Docs
              </button>
            )}

            <button
              type='button'
              className='btn-close ms-2'
              aria-label='Close'
              onClick={onCancel}
            />
          </div>
        </div>

        {/* Body with PDF-style layout (Neutral, no loud colors) */}
        <div className='flex-grow-1 overflow-auto p-4' style={{background: '#f9f9fa'}}>
          <div
            className='bg-white p-4 border rounded shadow-xs mx-auto'
            style={{maxWidth: '960px', borderColor: '#e4e6ef'}}
          >
            {/* Header Document Banner */}
            <div
              className='p-3 mb-4 rounded border'
              style={{background: '#f8f9fa', borderColor: '#dee2e6'}}
            >
              <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                <div>
                  <h4 className='fw-bold text-dark mb-1'>Near Miss Report</h4>
                  <div className='text-muted fs-7'>
                    Report No: <strong className='text-dark'>{record.reportNumber || '-'}</strong>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    Vessel: <strong className='text-dark'>{record.vesselName || '-'}</strong>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    Status: <strong className='text-dark'>{statusLabel}</strong>
                  </div>
                </div>
                <div className='text-muted fs-8 text-end'>
                  <div>Dexpert Fleet Management</div>
                </div>
              </div>
            </div>

            {/* 1. Report Details */}
            {renderSection(
              'Report Details',
              <>
                {renderRow('Report Number', record.reportNumber)}
                {renderRow('Vessel', record.vesselName)}
                {renderRow('Status', statusLabel)}
                {renderRow('Report Issued By', record.reportIssuedBy)}
                {renderRow('Date Issued', record.dateIssued)}
                {renderRow('Reported By', record.reportedBy)}
                {renderRow('Reported To', record.reportedTo)}
                {renderRow(
                  'Date Reported',
                  record.dateReported
                    ? `${record.dateReported}${record.timeReported ? ' ' + record.timeReported : ''}`
                    : '-'
                )}
              </>
            )}

            {/* 2. Occurrence Details */}
            {renderSection(
              'Occurrence Details',
              <>
                {renderRow(
                  'Date of Occurrence',
                  record.dateOfOccurrence
                    ? `${record.dateOfOccurrence}${record.timeOfOccurrence ? ' ' + record.timeOfOccurrence : ''}`
                    : '-'
                )}
                {renderRow('Position / Location', record.locationPosition)}
                {renderRow('Occurrence Types', occurrenceTypesValue)}
                {renderRow('Conditions', conditionsValue)}
                {renderRow('Location of Occurrence', locationValue)}
                {renderRow(
                  'Personal Injury Form Completed',
                  record.personalInjuryFormCompleted == null
                    ? '-'
                    : record.personalInjuryFormCompleted
                    ? 'Yes'
                    : 'No'
                )}
                {renderRow(
                  'Any Statements Attached',
                  record.anyStatementsAttached == null
                    ? '-'
                    : record.anyStatementsAttached
                    ? 'Yes'
                    : 'No'
                )}
              </>
            )}

            {/* 3. Description of Occurrence */}
            {renderDescSection('Description of Occurrence', record.descriptionOfOccurrence)}

            {/* 4. Analysis of Occurrence */}
            {renderSection(
              'Analysis of Occurrence',
              <>
                {renderRow('Substandard Acts', actsValue)}
                {renderRow('Substandard Conditions', conditionsSubValue)}
              </>
            )}

            {/* 5. Persons Involved */}
            {renderSection(
              'Persons Involved',
              <>
                {renderRow('Persons Injured', record.personsInjured)}
                {renderRow('Persons Involved', record.personsInvolved)}
                {renderRow('Persons Witness', record.personsWitness)}
                {renderRow('Type of Injury', record.typeOfInjury)}
              </>
            )}

            {/* 6. Causes & Corrective Actions */}
            {renderSection(
              'Causes & Corrective Actions',
              <>
                {renderRow('Immediate / Basic Cause', record.immediateBasicCause)}
                {renderRow('Root Cause', record.rootCause)}
                {renderRow('Corrective Actions Proposed', record.correctiveActionsProposed)}
                {renderRow('Preventative Actions Proposed', record.preventativeActionsProposed)}
              </>
            )}

            {/* 7. Review */}
            {renderSection(
              'Review',
              <>
                {renderRow('Objective Evidence', record.objectiveEvidence)}
                {renderRow(
                  'DPA Conclusions & Recommendations',
                  record.dpaConclusionsAndRecommendations
                )}
              </>
            )}

            {/* 8. Supporting Documents */}
            {renderSection(
              'Supporting Documents',
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid #dee2e6',
                    fontSize: '12px',
                  }}
                >
                  {record.supportingDocsPrimaryPath && onOpenSupporting && record.id ? (
                    <button
                      type='button'
                      className='btn btn-light-primary btn-sm d-inline-flex align-items-center'
                      onClick={() => onOpenSupporting(record.id!, record.reportNumber ?? null)}
                    >
                      <KTSVG
                        path='/media/icons/duotune/files/fil003.svg'
                        className='svg-icon-4 me-1'
                      />
                      View Uploaded Supporting Documents
                    </button>
                  ) : (
                    <span className='text-muted'>No supporting documents uploaded.</span>
                  )}
                </td>
              </tr>
            )}

            {/* Footer document stamp */}
            <div className='mt-3 pt-2 text-muted fs-8 border-top text-center'>
              Dexpert Fleet Management System &bull; Near Miss Report
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className='border-top d-flex justify-content-end align-items-center px-4 py-3 flex-shrink-0 bg-light'>
          <button type='button' className='btn btn-light btn-sm' onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
